/**
 * CTASection Component
 * ====================
 * Final call-to-action banner
 * ALL content from Blueprint - NO hardcoded text or routes
 * 
 * Expected config format (ui.home.cta):
 * {
 *   title: "Pronto a iniziare?",
 *   subtitle: "Registrati gratuitamente",
 *   button: "Inizia ora",
 *   route: "/register",
 *   image: "https://..."
 * }
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useBlueprint, BlueprintSection } from '../../context/BlueprintContext';
import { ArrowRight } from 'lucide-react';

const CTASection = () => {
  const { homepage, getRoute } = useBlueprint();
  const cta = homepage?.cta || {};
  
  // Safe text extraction helper
  const safeText = (value, fallback = '') => {
    if (!value) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.text) return safeText(value.text, fallback);
      if (value.label) return safeText(value.label, fallback);
      return fallback;
    }
    return fallback;
  };
  
  // Resolve route
  const resolveLink = (link) => {
    if (!link) return '/register';
    if (typeof link === 'string' && link.startsWith('/')) return link;
    if (typeof link === 'object') return link.route || link.url || '/register';
    return getRoute(link, '/register');
  };
  
  // Safe values
  const title = safeText(cta.title, '');
  const subtitle = safeText(cta.subtitle, '');
  const buttonText = safeText(cta.button, 'Inizia ora');
  const buttonLink = resolveLink(cta.route);
  
  // If no title, don't render
  if (!title) {
    return null;
  }
  
  return (
    <BlueprintSection name="cta">
      <section 
        className="relative py-14 px-8 border-t border-[#D8D4CE]"
        style={{
          backgroundColor: cta.image ? 'transparent' : '#F0EDE7'
        }}
      >
        {/* Background image if provided */}
        {cta.image && (
          <div className="absolute inset-0 z-0">
            <img 
              src={cta.image} 
              alt="" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/50" />
          </div>
        )}
        
        <div className={`sr-container relative z-10 ${cta.image ? 'text-white' : ''}`}>
          <div className="flex items-center justify-between gap-12 flex-wrap">
            {/* Content */}
            <div>
              <h2 className={`font-[var(--ff-h)] text-[clamp(28px,3vw,46px)] font-semibold leading-[1.15] tracking-[-0.4px] max-w-[460px] ${
                cta.image ? 'text-white' : 'text-[var(--dark)]'
              }`}>
                {title}
              </h2>
              {subtitle && (
                <p className={`mt-3 text-base ${cta.image ? 'text-white/80' : 'text-[#555]'}`}>
                  {subtitle}
                </p>
              )}
            </div>
            
            {/* Button */}
            <Link 
              to={buttonLink} 
              className={`sr-btn-primary ${cta.image ? 'bg-white text-[var(--dark)] hover:bg-white/90' : ''}`}
            >
              {buttonText}
              <ArrowRight size={15} className="arrow" />
            </Link>
          </div>
        </div>
      </section>
    </BlueprintSection>
  );
};

export default CTASection;
